import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Link from 'gatsby-link'
import styled, { keyframes } from 'styled-components'

const pulseBackground = keyframes`
  0%,
  100% {
    background-position: 0% 0%;
  }

  50% {
    background-position: 0% 100%;
  }
`

const backgroundChooser = ({ theme, pulse, disabled }) => {
  let gradient = theme.color.Button.bg.join(',')
  if (disabled) gradient = theme.color.Button.bgDisabled.join(',')
  if (pulse)
    gradient = theme.color.Button.bg[0] + ',' + theme.color.Button.bgHover[1]

  return `linear-gradient(to bottom, ${gradient})`
}

const Container = styled(Link)`
  animation: ${(p) => (p.pulse ? pulseBackground : 'none')} 2s infinite;
  animation-timing-function: cubic-bezier(0.455, 0.03, 0.515, 0.955);
  align-items: center;
  background: ${backgroundChooser};
  background-size: ${(p) => (p.pulse ? '100% 200%' : 'auto')};
  border-radius: ${(props) => props.theme.radius(-2)};
  border: 0.2rem solid ${(p) => p.theme.color.Button.border};
  box-shadow: 0.2rem -0.2rem 0 rgba(0, 0, 0, 0.5),
    inset 0 0.2rem 0
      ${(props) =>
        props.disabled
          ? props.theme.color.Button.shadowInsetDisabled
          : props.theme.color.Button.shadowInset};
  display: inline-flex;
  justify-content: center;
  letter-spacing: 0.1rem;
  margin-top: ${(props) => props.theme.spacing(-2)};
  padding: 0.2rem ${(props) => props.theme.spacing(0)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  position: relative;

  &:hover {
    background: linear-gradient(
      to bottom,
      ${(props) =>
        props.theme.color.Button.bgHover
          ? props.theme.color.Button.bgHover.join(',')
          : props.theme.color.Button.bg.join(',')}
    );
    box-shadow: 0.2rem -0.2rem 0 rgba(0, 0, 0, 0.5),
      inset 0 0.2rem 0
        ${(props) =>
          props.theme.color.Button.shadowInsetHover
            ? props.theme.color.Button.shadowInsetHover
            : props.theme.color.Button.shadowInset};
  }

  &:active {
    background: linear-gradient(
      to bottom,
      ${(props) =>
        props.theme.color.Button.bgActive
          ? props.theme.color.Button.bgActive.join(',')
          : props.theme.color.Button.bg.join(',')}
    );
    box-shadow: 0.2rem -0.2rem 0 rgba(0, 0, 0, 0.5),
      inset 0 0.2rem 0
        ${(props) =>
          props.theme.color.Button.shadowInsetActive
            ? props.theme.color.Button.shadowInsetActive
            : props.theme.color.Button.shadowInset};
  }
`

const ContainerSmall = Container.extend`
  border-radius: ${(props) => props.theme.radius(-2)};
  border: 0.1rem solid #fff;
`

const contentFontSizeChooser = ({ theme, size }) => {
  switch (size) {
    case 'large':
      return theme.fontSize(3.5)
    case 'small':
      return theme.fontSize(1)
    default:
      return theme.fontSize(2)
  }
}

const Content = styled.div`
  color: ${(props) =>
    props.disabled
      ? props.theme.color.Button.copyDisabled
      : props.theme.color.Button.copy};
  font-family: ${(props) => props.theme.fontFamily.primary};
  font-size: ${contentFontSizeChooser};
  line-height: 100%;
  padding: ${(props) => props.theme.spacing(-3)} 0;
  text-transform: uppercase;
`

const Images = styled.div`
  align-items: center;
  display: flex;
  filter: ${(props) => (props.disabled ? 'brightness(40%)' : 'none')};
  justify-content: center;
  opacity: ${(props) => (props.disabled ? 0.8 : 1)};
  padding: ${(props) => props.theme.spacing(-2)} 0;
`
const PreImages = styled(Images)`
  margin-right: ${(props) => props.theme.spacing(0)};
`
const PostImages = styled(Images)`
  margin-left: ${(props) => props.theme.spacing(0)};
`

const imageMaxHeightChooser = ({ theme, size }) => {
  switch (size) {
    case 'large':
      return theme.fontSize(3.5)
    case 'small':
      return theme.fontSize(1.5)
    default:
      return theme.fontSize(2.5)
  }
}

const Image = styled.img`
  margin-right: ${(props) => props.theme.spacing(-1)};
  max-height: ${imageMaxHeightChooser};

  &:last-child {
    margin-right: 0;
  }
`

/**
 * Styled common button. Utilizes Gatby's `Link` component.
 */
export default class Button extends Component {
  static propTypes = {
    /** Disable clicking and apply alternative styling. */
    disabled: PropTypes.bool,

    /** Mark destination as external. **/
    external: PropTypes.bool,

    /** Size of button. **/
    size: PropTypes.oneOf(['small', 'normal', 'large']),

    /** List of image paths to display before the content. */
    preImages: PropTypes.arrayOf(PropTypes.string),

    /** List of image paths to display after the content. */
    postImages: PropTypes.arrayOf(PropTypes.string),

    /** Draw attention to the component. */
    pulse: PropTypes.bool,

    /** Use a native <button> element */
    useButtonTag: PropTypes.bool,
  }

  static defaultProps = {
    disabled: false,
    external: false,
    postImages: [],
    preImages: [],
    pulse: false,
    size: 'normal',
    useButtonTag: false,
  }

  render() {
    const {
      children,
      disabled,
      external,
      postImages,
      preImages,
      pulse,
      size,
      buttonText = null,
      onClick = () => {},
      useButtonTag,
      ...props
    } = this.props

    const SizedContainer = size === 'small' ? ContainerSmall : Container
    const FinalContainer = useButtonTag
      ? SizedContainer.withComponent('button')
      : external
      ? SizedContainer.withComponent('a')
      : SizedContainer

    return (
      <FinalContainer
        size={size}
        disabled={disabled}
        pulse={pulse}
        onClick={onClick}
        {...props}
      >
        {preImages.length > 0 && (
          <PreImages disabled={disabled}>
            {preImages.map((image, i) => (
              <Image key={`preImage-${i}`} src={image} />
            ))}
          </PreImages>
        )}
        <Content size={size} disabled={disabled}>
          {buttonText ? buttonText : children}
        </Content>
        {postImages.length > 0 && (
          <PostImages disabled={disabled}>
            {postImages.map((image, i) => (
              <Image size={size} key={`postImage-${i}`} src={image} />
            ))}
          </PostImages>
        )}
      </FinalContainer>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/Button.js